/**
 * Line entity → `RenderableEntity` adapter.
 *
 * Reads the start / end coords from the property bag (matches the SVG
 * renderer's keys: "Start X / Y", "End X / Y") and applies the locked-
 * in coordinate origin offset so the GPU receives Float32-safe values.
 *
 * Color resolution (BYLAYER → BYBLOCK → explicit) and lineweight live
 * in the calling pipeline (`buildRenderable` in `index.ts`); this
 * adapter is geometry-only.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import { readNumber } from "../../scene/renderHelpers";
import type { RenderableEntity, Vec3 } from "../types";

import {
  buildRenderableShell,
  type AdapterContext,
} from "./adapterContext";
import { buildLineSceneGeometry } from "./sceneGeometryBuilders";

/** Adapter for DXF LINE / `Line` entity type. */
export function lineToRenderable(
  entity: EntityPreview,
  ctx: AdapterContext,
): RenderableEntity {
  const aRaw: Vec3 = {
    x: readNumber(entity.properties, "Start X"),
    y: readNumber(entity.properties, "Start Y"),
    z: readNumber(entity.properties, "Start Z"),
  };
  const bRaw: Vec3 = {
    x: readNumber(entity.properties, "End X"),
    y: readNumber(entity.properties, "End Y"),
    z: readNumber(entity.properties, "End Z"),
  };

  const { geometry, bounds } = buildLineSceneGeometry(aRaw, bRaw);

  return buildRenderableShell(entity, ctx, geometry, bounds);
}
